import React, { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { authSelector } from '../features/auth/slice';
import { ArrowRightLeft, Users, Wallet, RefreshCw, Pencil, Check, X, ChevronLeft, ChevronRight } from 'lucide-react';

const PAGE_SIZE = 20;

function fmt(n) {
  return Number(n || 0).toLocaleString();
}

function MonthBadge({ month }) {
  if (!month) return null;
  const d = new Date(month);
  return (
    <span className="text-xs px-2 py-0.5 rounded-full bg-sky-500/15 text-sky-400 font-medium">
      {d.getFullYear()}.{String(d.getMonth() + 1).padStart(2, '0')}
    </span>
  );
}

export default function CarryoverPage() {
  const { me } = useSelector(authSelector.selectAuthState);
  const navigate = useNavigate();
  useEffect(() => {
    if (me && me.superAdminYn !== 'Y') navigate('/dashboard', { replace: true });
  }, [me, navigate]);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [editRow, setEditRow] = useState(null);
  const [editVal, setEditVal] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await axios.get('/ByClient/carryover');
      setData(res);
      setPage(1);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const startEdit = (row) => { setEditRow(row); setEditVal(String(row.prevAmount || 0)); };
  const cancelEdit = () => { setEditRow(null); setEditVal(''); };

  const saveEdit = async () => {
    setSaving(true);
    try {
      await axios.put('/ByClient/byClientLatestMonth/setPrevAmount', {
        clientId: editRow.clientId,
        calculateMonth: editRow.calculateMonth,
        prevAmount: Number(editVal) || 0,
      });
      cancelEdit();
      fetchData();
    } catch (e) {
      alert(e.response?.data?.errorMessage || '수정 실패');
    }
    setSaving(false);
  };

  const allRows = data?.rows || [];
  const totalPages = Math.ceil(allRows.length / PAGE_SIZE);
  const rows = allRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div>
      {/* 타이틀 */}
      <div className="flex mb-8 justify-center items-center relative">
        <div className="text-xl sm:text-2xl font-bold text-white tracking-tight">이월액 현황</div>
        <button
          onClick={fetchData}
          className="absolute right-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-white/5 hover:bg-white/10 text-slate-400 hover:text-white transition-colors">
          <RefreshCw size={13} />
          새로고침
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-amber-400" />
        </div>
      ) : (
        <>
          {/* 총액 + 인원 요약 */}
          <div className="grid grid-cols-2 gap-3 mb-6">
            <div className="bg-[#182334] border border-white/[0.08] rounded-2xl p-5">
              <div className="flex items-center gap-2 mb-3">
                <Wallet size={15} className="text-amber-400" />
                <span className="text-xs text-slate-400">총 이월액</span>
              </div>
              <div className="text-3xl font-bold text-amber-400">
                {fmt(data?.totalCarryover)}
                <span className="text-base text-slate-500 ml-1.5">원</span>
              </div>
            </div>
            <div className="bg-[#182334] border border-white/[0.08] rounded-2xl p-5">
              <div className="flex items-center gap-2 mb-3">
                <Users size={15} className="text-sky-400" />
                <span className="text-xs text-slate-400">이월 권리사 수</span>
              </div>
              <div className="text-3xl font-bold text-white">
                {data?.count || 0}
                <span className="text-base text-slate-500 ml-1.5">명</span>
              </div>
            </div>
          </div>

          {/* 테이블 */}
          {allRows.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 text-slate-500">
              <ArrowRightLeft size={32} className="mb-3 opacity-30" />
              <p className="text-sm">현재 이월액이 있는 권리사가 없습니다.</p>
            </div>
          ) : (
            <>
              <div className="bg-[#182334] border border-white/[0.08] rounded-2xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-white/[0.06]">
                        <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wide">권리사</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wide">정산월</th>
                        <th className="text-right px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wide">당월 발생액</th>
                        <th className="text-right px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wide">전월이월</th>
                        <th className="text-right px-4 py-3 text-xs font-semibold text-amber-400 uppercase tracking-wide">이월액</th>
                        <th className="px-4 py-3" />
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row, i) => {
                        const isEditing = editRow?.clientId === row.clientId && editRow?.calculateMonth === row.calculateMonth;
                        return (
                          <tr key={row.clientId} className={`border-b border-white/[0.04] hover:bg-white/[0.02] transition-colors ${i === rows.length - 1 ? 'border-0' : ''}`}>
                            <td className="px-4 py-3">
                              <div className="font-medium text-white">{row.clientName}</div>
                              <div className="text-xs text-slate-500">{row.loginId}</div>
                            </td>
                            <td className="px-4 py-3"><MonthBadge month={row.calculateMonth} /></td>
                            <td className="px-4 py-3 text-right text-slate-300">{fmt(row.amount)}</td>
                            <td className="px-4 py-3 text-right">
                              {isEditing ? (
                                <div className="flex items-center justify-end gap-1.5">
                                  <input
                                    type="number"
                                    value={editVal}
                                    onChange={e => setEditVal(e.target.value)}
                                    className="w-28 px-2 py-1 rounded-lg border border-sky-500/40 bg-sky-500/10 text-sky-300 text-right text-sm outline-none"
                                    autoFocus
                                  />
                                  <button onClick={saveEdit} disabled={saving} className="p-1.5 rounded-lg bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 transition-colors">
                                    <Check size={13} />
                                  </button>
                                  <button onClick={cancelEdit} className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-slate-400 transition-colors">
                                    <X size={13} />
                                  </button>
                                </div>
                              ) : (
                                <span className="text-slate-300">{fmt(row.prevAmount)}</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-right font-bold text-amber-400">{fmt(row.nextAmount)}</td>
                            <td className="px-4 py-3 text-right">
                              {!isEditing && (
                                <button
                                  onClick={() => startEdit(row)}
                                  title="전월이월 수정"
                                  className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-slate-500 hover:text-slate-300 transition-colors">
                                  <Pencil size={13} />
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* 페이징 */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-4">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-slate-400 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                    <ChevronLeft size={16} />
                  </button>
                  <span className="text-sm text-slate-400">
                    <span className="text-white font-semibold">{page}</span> / {totalPages}
                    <span className="text-slate-600 ml-2">({allRows.length}명)</span>
                  </span>
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-slate-400 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                    <ChevronRight size={16} />
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
